import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ButtonComponent } from '../../molecules/button/button.component';

import MenuItem from '../../../shared/interfaces/menuItems.interface';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import {
  CaasInpageNav,
  CaasInternalLink,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { Router } from '@angular/router';
import { CTAThemes } from '../../../shared/enums/inpage-navigation-type.enums';
import { BehaviorSubject } from 'rxjs';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { LinkComponent } from '../../atoms/link/link.component';
import LinkType from '../../../shared/enums/link-type.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { CapitalizePipe } from '../../capitalize.pipe';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-inpage-navigation',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    LinkComponent,
    CapitalizePipe,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './inpage-navigation.component.html',
  styleUrls: ['./inpage-navigation.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class InpageNavigationComponent implements OnChanges, AfterViewChecked {
  @ViewChild('slider') slider: ElementRef = {} as ElementRef;
  @ViewChild('inpageNavigation') inpageNavigation: ElementRef =
    {} as ElementRef;
  @Input() caasData?: CaasInpageNav;

  @Input() inpageMenu: Array<MenuItem> = [];
  @Input() offsetY: number = 60;
  @Input() buttonLabel: string = 'Kontakt';
  @Input() buttonTitle: string = '';
  @Input() buttonIcon: string = 'envelope';
  @Input() titleText: string = 'Thema wählen';
  @Input() CTAButton: boolean = false;
  @Input() CTALink: string = '';
  @Input() CTAFragment?: string;
  @Input() CTALinkType: 'internal' | 'external' = 'internal';
  @Input() CTATheme?: CTAThemes = CTAThemes.WRITTEN_CONTACT;
  @Input() isSticky: boolean = false;

  public readonly buttonType = ButtonType;
  public readonly CTAThemes = CTAThemes;
  readonly LinkType = LinkType;
  readonly ButtonType = ButtonType;

  public isDown: boolean = false;
  public startX: number = 0;
  public scrollLeft: number = 0;
  // public activeSectionId: string = '';
  public isSmallScreen: boolean = false;
  public isLoading = true;
  private readonly isScrollBlocked: boolean = false;

  activeSectionId: BehaviorSubject<string> = new BehaviorSubject('');
  readonly HrefType = HrefType;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.activeSectionId.next(this.document.querySelector('[id]')?.id || '');
    this.checkInnerWidth();
    if (changes['caasData']) {
      this.setCaasContent();
    }
    if (changes['inpageMenu']) {
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  ngAfterViewChecked(): void {
    if (!this.caasData) {
      this.setCTATheme();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.checkInnerWidth();
  }

  @HostListener('window:scroll')
  onScroll() {
    this.isSticky =
      this.inpageNavigation.nativeElement.getBoundingClientRect().top <=
        this.offsetY &&
      this.inpageNavigation.nativeElement.getBoundingClientRect().bottom >= 0;
    if (this.isSticky) {
      if (!this.isScrollBlocked) {
        this.setActiveSliderItem();
      }
    }
  }
  setActiveSliderItem() {
    const sections = Array.from(
      this.document.querySelectorAll<HTMLElement>('main hdi-section > [id]')
    ).filter(
      (section) =>
        section.offsetParent !== null && // Ensure the section is visible
        section.id.trim() !== '' // Ensure the section has a valid non-empty ID
    );

    let activeSection: HTMLElement | null = null;

    for (const section of sections) {
      const rect = section.getBoundingClientRect();
      if (rect.top - 55 <= this.offsetY && rect.bottom > this.offsetY) {
        activeSection = section;
        break;
      }
    }

    if (activeSection && this.activeSectionId.value !== activeSection.id) {
      this.activeSectionId.next(activeSection.id);
    }
  }
  startDrag(event: DragEvent) {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='; // 1x1 pixel transparent gif
    event.dataTransfer?.setDragImage(img, 0, 0);
  }

  startDragging(event: DragEvent) {
    event.stopPropagation();
    this.isDown = true;
    this.startX = event.clientX - this.slider.nativeElement.offsetLeft;
    this.scrollLeft = this.slider.nativeElement.scrollLeft;
  }

  moveEvent(event: DragEvent) {
    if (!this.isDown) return;
    event.preventDefault();
    const x = event.clientX - this.slider.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 18; //scroll-fast
    this.slider.nativeElement.scrollLeft = this.scrollLeft - walk;
  }

  stopDragging() {
    this.isDown = false;
  }

  //Check inner width to change Button Type to Icon only on small screens
  checkInnerWidth() {
    if (
      this.document.defaultView &&
      this.document.defaultView.innerWidth < 990
    ) {
      this.isSmallScreen = true;
    } else {
      this.isSmallScreen = false;
    }
  }

  setCaasContent() {
    if (this.caasData?.formData?.st_type?.value?.identifier) {
      safeAssign(
        () => {
          this.CTATheme = this.setCaasCTATheme(
            this.caasData?.formData?.st_type?.value?.identifier || ''
          );
        },
        'Set CTA Theme',
        this.errorHandler
      );

      if (this.CTATheme) {
        this.setCTATheme();
        this.setCaasCTAThemeLink();
      } else {
        this.setCustomCTATheme();
      }
    }
  }

  getCaasLink(
    linkType: string,
    externalLink: CaasSimpleField,
    internalLink: CaasInternalLink
  ): string {
    if (linkType === 'external') {
      return externalLink?.value ?? '/not-found';
    } else {
      return internalLink?.value?.name.toLowerCase() ?? '/not-found';
    }
  }

  setCTATheme() {
    switch (this.CTATheme) {
      case this.CTAThemes.PERSONAL_CONTACT:
        this.buttonLabel = 'Kontakt';
        this.buttonIcon = 'user-headset';
        break;
      case this.CTAThemes.ONLINE_COMPLETION:
      case this.CTAThemes.ONLINE_OFFER:
        this.buttonLabel = 'Online rechnen';
        this.buttonIcon = 'calculator';
        break;
      case this.CTAThemes.DAMAGE:
        this.buttonLabel = 'Schaden melden';
        this.buttonIcon = 'bolt-lightning';
        break;
      case this.CTAThemes.TECHNICAL:
        this.buttonLabel = 'Technik-Hotline';
        this.buttonIcon = 'laptop';
        break;
      case this.CTAThemes.FEEDBACK:
        this.buttonLabel = 'Feedback';
        this.buttonIcon = 'comment-dots';
        break;
      default:
        this.buttonLabel = 'Kontakt';
        this.buttonIcon = 'envelope';
        break;
    }
  }

  setCaasCTATheme(theme: string): CTAThemes | undefined {
    switch (theme) {
      case 'technical_issue':
        return CTAThemes.TECHNICAL;
      case 'contact_mail':
        return CTAThemes.WRITTEN_CONTACT;
      case 'contact_personal':
        return CTAThemes.PERSONAL_CONTACT;
      case 'online_contract':
        return CTAThemes.ONLINE_COMPLETION;
      case 'online_offer':
        return CTAThemes.ONLINE_OFFER;
      case 'damage_report':
        return CTAThemes.DAMAGE;
      case 'feedback':
        return CTAThemes.FEEDBACK;
      case 'custom':
        return undefined;
      default:
        return CTAThemes.WRITTEN_CONTACT;
    }
  }

  setCaasCTAThemeLink() {
    if (this.caasData?.formData?.st_link_internal?.value) {
      let linkType = '';
      safeAssign(
        () => {
          linkType =
            this.caasData?.formData?.st_link_internal?.value?.formData
              ?.lt_link_type?.value.identifier || '';
        },
        'Set CTA Theme Link',
        this.errorHandler
      );

      if (linkType === 'external') {
        this.CTALinkType = 'external';
        safeAssign(
          () => {
            this.CTALink =
              this.caasData?.formData?.st_link_internal?.value?.formData
                ?.lt_link_external?.value || '';
          },
          'Set CTALinkType external',
          this.errorHandler
        );

        safeAssign(
          () => {
            this.CTAFragment =
              this.caasData?.formData?.st_link_internal?.value?.formData
                ?.st_anchor_name?.value || undefined;
          },
          'Set CTAFragment',
          this.errorHandler
        );
      } else if (linkType === 'internal') {
        this.CTALinkType = 'internal';
        let identifier = '';
        safeAssign(
          () => {
            identifier =
              this.caasData?.formData?.st_link_internal?.value?.formData
                ?.lt_link_internal?.value?.identifier ?? '';
          },
          'Set CTALinkType internal',
          this.errorHandler
        );

        safeAssign(
          () => {
            this.CTALink =
              this.caasRoutesService.getSeoRouteByIdentifier(
                identifier,
                this.router.config
              )?.path ?? '';
          },
          'Set CTALink',
          this.errorHandler
        );
      }
    }
    if (this.caasData?.formData?.st_link_external?.value) {
      if (
        this.caasData?.formData?.st_link_external?.value?.formData?.lt_link_type
          ?.value?.identifier === 'external'
      ) {
        this.CTALinkType = 'external';
        safeAssign(
          () => {
            this.CTALink =
              this.caasData?.formData?.st_link_external?.value?.formData
                ?.lt_link_external?.value ?? '/404';
          },
          'Set CTALink',
          this.errorHandler
        );
      } else {
        this.CTALinkType = 'internal';
        safeAssign(
          () => {
            this.CTALink =
              this.caasRoutesService.getSeoRouteByIdentifier(
                this.caasData?.formData?.st_link_external?.value?.formData
                  ?.lt_link_internal?.value?.identifier ?? '',
                this.router.config
              )?.path ?? '';
          },
          'Set CTALink',
          this.errorHandler
        );
      }
    }
  }

  setCustomCTATheme() {
    if (this.caasData?.formData.st_link_custom.value) {
      safeAssign(
        () => {
          this.buttonLabel =
            this.caasData?.formData?.st_link_custom?.value?.formData
              ?.lt_link_label?.value ?? '';
        },
        'Set buttonLabel',
        this.errorHandler
      );
      safeAssign(
        () => {
          this.buttonTitle =
            this.caasData?.formData?.st_link_custom?.value?.formData
              ?.lt_link_title?.value ?? '';
        },
        'Set buttonTitle',
        this.errorHandler
      );
      safeAssign(
        () => {
          this.buttonIcon =
            this.caasData?.formData?.st_link_custom?.value?.formData?.lt_icon
              ?.value ?? 'envelope';
        },
        'Set buttonIcon',
        this.errorHandler
      );

      if (
        this.caasData?.formData.st_link_custom.value.formData?.lt_link_type
          .value.identifier === 'external'
      ) {
        this.CTALinkType = 'external';
        safeAssign(
          () => {
            this.CTALink =
              this.caasData?.formData?.st_link_custom?.value?.formData
                ?.lt_link_external?.value ?? '/not-found';
          },
          'Set CTALink',
          this.errorHandler
        );
      } else {
        this.CTALinkType = 'internal';
        safeAssign(
          () => {
            this.CTALink =
              '/' +
              this.caasRoutesService.getSeoRouteByIdentifier(
                this.caasData?.formData?.st_link_custom?.value?.formData
                  ?.lt_link_internal?.value?.identifier ?? '',
                this.router.config
              )?.path;
          },
          'Set CTALink',
          this.errorHandler
        );
      }
    }
  }
}
